/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
// NPM Imports
import React from "react";
import {
  Entity,
  EditorState,
  ContentState,
  convertFromRaw,
  convertToRaw,
  RichUtils,
  SelectionState,
} from "draft-js";
import moment from "moment";
import uuid from "react-native-uuid";
import sanitizeHtml from "sanitize-html";
import { cloneDeep } from "lodash";
import store from "../store";

// Components Imports
import HTMLtoDraft from "./HTMLtoDraft/HTMLtoDraft";
import getEditorHTML from "../components/RevvEditor/getEditorHtml";
import getCompositeDecorator from "../components/CustomRevvEditor/decorator";

// Internal Imports
import { COMMON, DOCUMENTS, DRAFT, GRID } from "../constants";
import { readDataFromLocalStorage, numberForamting } from "./globalUtils";
import { getSelectedText } from "./draftUtils";
import { getFieldValue } from "./formFieldUtils";
import { tableAdapterConversion } from "./tableUtils";
import { convertSectionRawToDraft } from "./components";
import { updateFormulaWhileCloning } from "./tableFormulaUtils";

import { DOCUMENT_BLOCKS } from "../components/DocumentV2/constants";
import { USER_ROLES } from "../constants/roleList";
import { SignerConfig } from "../payloadObjects/signatoryPayload";

// Destructring
const { DOCUMENT_MODE, COMPONENT_FEATURE, DOCUMENT_COMPONENT_TYPES } =
  DOCUMENTS;
const { DEAL_LABELS, CURRENCY_SYMBOL } = COMMON;
const { BLANK_HTML } = DRAFT;
const { DEALTABLE_COLUMN_TYPES } = GRID;
// const { APP_FLAGS, , DEAL_LABELS } = COMMON;
export const getLayoutAndComponents = structure => {
  const layout = [];
  const components = {};
  if (Array.isArray(structure)) {
    for (let i = 0; i < structure.length; i += 1) {
      const rowIDs = [];
      if (Array.isArray(structure[i])) {
        for (let j = 0; j < structure[i].length; j += 1) {
          const component = structure[i][j];
          let componentId = component
            ? component.component_id || component.id
            : null;
          const content =
            component && component.content ? component.content : component;

          // check the component if it is a empty container
          if (
            content.type === DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER ||
            content.section_type === DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER
          ) {
            // do not push empty containers
            componentId = null;
          }

          // check component id
          if (componentId) {
            rowIDs.push(componentId);
            // check if the components is coverpage
            // if it is a coverpage, then get the layout and components for coverpage
            // this ensure the flow same in each component and at each level
            if (
              content.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE ||
              content.section_type === DOCUMENT_COMPONENT_TYPES.COVERPAGE
            ) {
              if (content.data && Array.isArray(content.data.data)) {
                const coverpageData = content.data.data.filter(
                  comp => comp !== null,
                );
                content.data = {
                  ...content.data,
                  ...getLayoutAndComponents(coverpageData),
                };
                delete content.data.data;
              }
            }

            // check if the components is header & footer
            // if it is a coverpage, then get the layout and components for header & footer
            // this ensure the flow same in each component and at each level
            if (
              content.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER ||
              content.section_type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
            ) {
              if (
                content.data.header &&
                Array.isArray(content.data.header.data) &&
                content.data.footer &&
                Array.isArray(content.data.footer.data)
              ) {
                const headerData = content.data.header.data.filter(
                  comp => comp !== null,
                );
                const footerData = content.data.footer.data.filter(
                  comp => comp !== null,
                );
                content.data = {
                  header: {
                    ...content.data.header,
                    ...getLayoutAndComponents(headerData),
                  },
                  footer: {
                    ...content.data.footer,
                    ...getLayoutAndComponents(footerData),
                  },
                };
                delete content.data.header.data;
                delete content.data.footer.data;
              }
            }

            components[componentId] = {
              content,
              dbRowIndex: component.drow,
              dbColIndex: component.dcol,
            };
          }
        }
      } else if (structure[i].component_id || structure[i].id) {
        const sectionId = structure[i].component_id || structure[i].id;
        // check the component if it is a empty container
        if (
          !(
            structure[i].type === DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER ||
            structure[i].section_type ===
              DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER
          )
        ) {
          rowIDs.push(sectionId);
          components[sectionId] = {
            content: structure[i],
            dbRowIndex: structure[i].drow,
            dbColIndex: structure[i].dcol,
          };
        }
      }

      // check if there is any id available in row
      if (rowIDs.length > 0) layout.push(rowIDs);
    }
  }

  return {
    layout,
    components,
  };
};

export const getComponentStructure = component => {
  const content =
    component && component.content ? component.content : component;
  // if it is a coverpage, then get the layout and components for coverpage
  // this ensure the flow same in each component and at each level
  if (
    content.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE ||
    content.section_type === DOCUMENT_COMPONENT_TYPES.COVERPAGE
  ) {
    if (content.data && Array.isArray(content.data.data)) {
      const coverpageData = content.data.data.filter(comp => comp !== null);
      content.data = {
        ...content.data,
        ...getLayoutAndComponents(coverpageData),
      };
      delete content.data.data;
    }
  }
  // if it is a coverpage, then get the layout and components for header & footer
  // this ensure the flow same in each component and at each level
  if (
    content.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER ||
    content.section_type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
  ) {
    if (
      content.data.header &&
      Array.isArray(content.data.header.data) &&
      content.data.footer &&
      Array.isArray(content.data.footer.data)
    ) {
      const headerData = content.data.header.data.filter(comp => comp !== null);
      const footerData = content.data.footer.data.filter(comp => comp !== null);
      content.data = {
        header: {
          ...content.data.header,
          ...getLayoutAndComponents(headerData),
        },
        footer: {
          ...content.data.footer,
          ...getLayoutAndComponents(footerData),
        },
      };
      delete content.data.header.data;
      delete content.data.footer.data;
    }
  }
  return {
    content,
  };
};

export const checkSectionIsTableV2GSheet = section => {
  let contentObj = section;
  if (Object.prototype.hasOwnProperty.call(section, "content")) {
    contentObj = section.content;
  }
  if (
    contentObj &&
    contentObj.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
    Object.prototype.hasOwnProperty.call(contentObj, "data") &&
    Object.prototype.hasOwnProperty.call(contentObj.data, "attributes") &&
    Object.prototype.hasOwnProperty.call(
      contentObj.data.attributes,
      "dataSource",
    ) &&
    contentObj.data.attributes.dataSource ===
      DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET
  ) {
    return true;
  }
  return false;
};

export const checkSectionIsLinkedQuoteTable = section => {
  let contentObj = section;
  if (Object.prototype.hasOwnProperty.call(section, "content")) {
    contentObj = section.content;
  }
  if (
    contentObj &&
    contentObj.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
    Object.prototype.hasOwnProperty.call(contentObj, "data") &&
    Object.prototype.hasOwnProperty.call(contentObj.data, "attributes") &&
    Object.prototype.hasOwnProperty.call(
      contentObj.data.attributes,
      "dataSource",
    ) &&
    contentObj.data.attributes.dataSource ===
      DOCUMENT_COMPONENT_TYPES.DEAL_TABLE
  ) {
    return true;
  }
  return false;
};

export const checkSectionIsLinkedToExcel = section => {
  let contentObj = section;
  if (Object.prototype.hasOwnProperty.call(section, "content")) {
    contentObj = section.content;
  }
  if (
    contentObj &&
    contentObj.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
    Object.prototype.hasOwnProperty.call(contentObj, "data") &&
    Object.prototype.hasOwnProperty.call(contentObj.data, "attributes") &&
    Object.prototype.hasOwnProperty.call(
      contentObj.data.attributes,
      "dataSource",
    ) &&
    contentObj.data.attributes.dataSource ===
      DOCUMENT_COMPONENT_TYPES.EXCEL_TABLE
  ) {
    return true;
  }
  return false;
};

export const checkForNumberOfLinkedSections = (sections, dataSource) => {
  // if dataSource param provided, method checks for that specific dataSource, else for all
  let noOfGsheetLinks = 0;
  let noOfQuoteTableLinks = 0;
  sections.forEach(section => {
    if (checkSectionIsTableV2GSheet(section)) noOfGsheetLinks += 1;
    if (checkSectionIsLinkedQuoteTable(section)) noOfQuoteTableLinks += 1;
  });
  if (dataSource === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
    return noOfGsheetLinks;
  }
  if (dataSource === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE) {
    return noOfQuoteTableLinks;
  }

  return noOfGsheetLinks + noOfQuoteTableLinks;
};

export const emptyContainer = () => ({
  id: uuid.v4(),
  type: DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER,
  lock: false,
  label: `Empty Container`,
  header: "",
  data: "",
  html: "",
});

export const emptyTextEditor = (mode, HTMLString, hasFocus = true) => {
  let contentState;
  let editorState;
  if (HTMLString === undefined) {
    editorState = EditorState.createEmpty();
    contentState = editorState.getCurrentContent();
  } else if (typeof HTMLString === "string") {
    contentState = HTMLtoDraft(HTMLString);
    // editorState = EditorState.createWithContent(
    //   contentState,
    //   getCompositeDecorator(),
    // );
  }
  const textEditorData = {
    data: contentState,
    // editorState,
  };
  return textEditorData;
};

export const emptyCell = hasFocus => {
  const cellData = {
    data: {
      ...emptyTextEditor(undefined, undefined, hasFocus),
    },
    attributes: {
      id: uuid.v4(),
    },
  };
  return cellData;
};

export const emptyEditor = mode => {
  const editorState = EditorState.createEmpty();
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.HTML,
    lock: false,
    label: "",
    header: "",
    data: editorState.getCurrentContent(),
    // editorState,
    html: BLANK_HTML,
  };
  return section;
};

export const removeSelection = editorState => {
  const currentContent = editorState.getCurrentContent();
  const firstBlock = currentContent.getBlockMap().first();
  const firstBlockKey = firstBlock.getKey();

  const selection = new SelectionState({
    anchorKey: firstBlockKey,
    anchorOffset: 0,
    focusKey: firstBlockKey,
    focusOffset: 0,
  });
  return EditorState.forceSelection(editorState, selection);
};

export const selectAllEditorText = editorState => {
  const currentContent = editorState.getCurrentContent();
  const firstBlock = currentContent.getBlockMap().first();
  const lastBlock = currentContent.getBlockMap().last();
  const firstBlockKey = firstBlock.getKey();
  const lastBlockKey = lastBlock.getKey();
  const lengthOfLastBlock = lastBlock.getLength();

  const selection = new SelectionState({
    anchorKey: firstBlockKey,
    anchorOffset: 0,
    focusKey: lastBlockKey,
    focusOffset: lengthOfLastBlock,
  });

  return EditorState.forceSelection(editorState, selection);
};

export const emptyPageNumber = mode => {
  const editorState = EditorState.createWithContent(
    ContentState.createFromText("#"),
    getCompositeDecorator(),
  );
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.PAGE_NUMBER,
    lock: false,
    label: "",
    header: "",
    data: editorState.getCurrentContent(),
    // editorState,
  };
  return section;
};

// accept components
export const checkIfPageNumberAdded = components => {
  let headerFooter = null;
  Object.keys(components).forEach(componentId => {
    if (
      components[componentId] &&
      components[componentId].content &&
      components[componentId].content.type ===
        DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
    ) {
      headerFooter = components[componentId];
    }
  });

  const result = { isPageNumberInHeader: false, isPageNumberInFooter: false };
  if (headerFooter && headerFooter.content && headerFooter.content.data) {
    const { header, footer } = headerFooter.content.data;
    if (header && header.components) {
      Object.keys(header.components).map(componentId => {
        if (
          header.components[componentId] &&
          header.components[componentId].content &&
          header.components[componentId].content.type ===
            DOCUMENT_COMPONENT_TYPES.PAGE_NUMBER
        ) {
          result.isPageNumberInHeader = true;
        }
        return result.isPageNumberInHeader;
      });
    }

    if (footer && footer.components) {
      Object.keys(footer.components).map(componentId => {
        if (
          footer.components[componentId] &&
          footer.components[componentId].content &&
          footer.components[componentId].content.type ===
            DOCUMENT_COMPONENT_TYPES.PAGE_NUMBER
        ) {
          result.isPageNumberInFooter = true;
        }
        return result.isPageNumberInFooter;
      });
    }
  }
  return result;
};

export const emptyTableData = (mode, count) => {
  let tableData = {
    attributes: {
      columnCount: 2,
      rowCount: 2,
      tableColumnDefs: [{ id: uuid.v4() }, { id: uuid.v4() }],
    },
    data: [
      {
        attributes: { columnCount: 2, id: uuid.v4() },
        data: [
          {
            ...emptyCell(true),
          },
          {
            ...emptyCell(false),
          },
        ],
      },
      {
        attributes: { columnCount: 2, id: uuid.v4() },
        data: [
          {
            ...emptyCell(false),
          },
          {
            ...emptyCell(false),
          },
        ],
      },
    ],
  };

  tableData = tableAdapterConversion(tableData);
  return tableData;
};

export const emptyTable = (mode, count) => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.TABLE,
    lock: false,
    label: `Table ${count + 1}`,
    header: "",
    data: emptyTableData(mode, count),
  };
  return section;
};

export const getSignatureConfigJson = () => [
  {
    id: 1,
    signer_email: "",
    err_msg: "",
    disabled: true,
    internal_user: false,
    first_name: "",
    last_name: "",
    signer_config: {
      signature_type: "FULLNAME",
      image_url: "",
      options: [
        {
          id: "signature",
          label: "Signature",
          checked: true,
          value: "",
        },
        // {
        //   id: "signerInitials",
        //   label: "Signer Initials",
        //   checked: false,
        //   value: "",
        // },
        {
          id: "name",
          label: "Signer Name",
          checked: false,
          value: "",
        },
        {
          id: "date",
          label: "Signing Date",
          checked: false,
          value: "",
        },
        // {
        //   id: "signingTitle",
        //   label: "Signing Title",
        //   checked: false,
        //   value: "",
        // },
        // {
        //   id: "signingCompany",
        //   label: "Signing Company",
        //   checked: false,
        //   value: "",
        // },
      ],
    },
  },
];

const getSignatureConfigJsonV2 = (
  tagID = 1,
  optionID = "signature",
  componentID,
  signerIdentityID,
  signer_config,
) => {
  const signerConfig = new SignerConfig();
  signerConfig.updateOptions(optionID, true, componentID).filterOptions();
  signerConfig.signature_type =
    signer_config && signer_config.signature_type
      ? signer_config.signature_type
      : signerConfig.signature_type;
  return {
    id: tagID,
    component_id: componentID,
    signer_identity_id: signerIdentityID,
    tag_type: "",
    signer_config: signerConfig.signer_config,
  };
};

export const emptySignatureSpace = count => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER,
    lock: false,
    label: `Signature ${count + 1}`,
    header: "",
    data: getSignatureConfigJson(),
  };
  return section;
};

export const emptySignatureSpaceV2 = (
  signerIndex,
  tagID,
  optionID,
  signerIdentityID,
  signer_config,
) => {
  const componentID = uuid.v4();
  const section = {
    id: componentID,
    type: DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER,
    lock: false,
    label: `Signature ${signerIndex + 1}`,
    header: "",
    justify_content: "flex-start",
    data: getSignatureConfigJsonV2(
      tagID,
      optionID,
      componentID,
      signerIdentityID,
      signer_config,
    ),
  };
  return section;
};

export const emptyPaymentButton = () => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.STRIPE_PAYMENT_BUTTON,
    lock: false,
    label: "Payment button",
    header: "",
    data: {
      amount: "",
      currency: { value: "USD", label: "$ - US Dollar (USD)" },
      payNowLabel: "Pay Now",
      editMode: false,
      editClick: false,
      paymentDone: false,
      stripeUserId: null,
    },
  };
  return section;
};

export const googleSheetSpace = count => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET,
    lock: false,
    label: `Linked Google Sheet ${count + 1}`,
    header: "",
    data: { data: [], attributes: {} },
    sheet: {},
    workSheet: "",
    rangeValue: "",
    autoTrim: false,
    connected: false,
    firstRowAsHeader: true,
  };
  return section;
};

export const emptyImage = count => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.IMAGE,
    lock: false,
    label: `Image ${count + 1}`,
    header: "",
    data: "",
    html: <b>Placeholder for FilePond</b>,
  };
  return section;
};

export const emptyDealTable = () => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.DEAL_TABLE,
    lock: false,
    label: DEAL_LABELS["deal-table-label"],
    header: "",
    data: [],
    html: "",
  };
  return section;
};

export const emptyQuoteTable = () => {
  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE,
    lock: false,
    label: "Quote Table",
    header: "",
    data: { group_summary: [], manage_column: [], quote_id: null },
    html: "",
  };
  return section;
};

export const emptyQuoteTablePlaceholder = components => {
  if (
    components &&
    checkForNumberOfLinkedSections(
      Object.values(components),
      DOCUMENT_COMPONENT_TYPES.DEAL_TABLE,
    ) > 0
  ) {
    return emptyDealTable();
  }

  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE_WRAPPER,
    lock: false,
    label: `${DEAL_LABELS["deal-table-label"]} Placeholder`,
    header: "",
    data: [],
    html: "",
  };
  return section;
};

export const pageBreak = () => ({
  id: uuid.v4(),
  type: DOCUMENT_COMPONENT_TYPES.PAGE_BREAK,
  lock: false,
  label: `Page Break`,
  header: "",
  data: "",
  html: "",
});

export const lineSpace = (space = 1) => ({
  id: uuid.v4(),
  type: DOCUMENT_COMPONENT_TYPES.LINE_SPACE,
  lock: false,
  label: `Empty space`,
  header: "",
  data: { id: `${space}x`, length: `${50 * space}`, unit: "px" },
  html: "",
});

export const headerFooter = mode => {
  const id = uuid.v4();

  const headerData = [[emptyEditor(mode)]];
  const footerData = [[emptyEditor(mode)]];
  const { layout: headerLayout, components: headerComponents } =
    getLayoutAndComponents(headerData);
  const { layout: footerLayout, components: footerComponents } =
    getLayoutAndComponents(footerData);
  const section = {
    id,
    type: DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER,
    lock: false,
    label: "Header & Footer",
    header: "",
    data: {
      header: {
        id: `header:${id}`,
        type: "Header",
        attributes: {
          background: "#0404e2",
          color: "#ff0001",
        },
        layout: headerLayout,
        components: headerComponents,
      },
      footer: {
        id: `footer:${id}`,
        type: "Footer",
        attributes: {
          background: "#0404e2",
          color: "#ff0001",
        },
        layout: footerLayout,
        components: footerComponents,
      },
    },
  };
  return section;
};

export const emptyCoverpage = mode => {
  const headingHTML =
    "<h1 class=header-one style='text-align:left;'>Document Title here</h1>";
  const subHeadingHTML =
    "<h3 class=header-three style='text-align:left;'>Sub Title here</h3>";
  const clientCompanyHTML = `
    <div>
      <p class=unstyled style='text-align:left;'>Prepared for:</p>
      <p class=unstyled style='text-align:left;'><strong>[Client.Company]</strong></p>
      <p class=unstyled><br></p>
      <p class=unstyled style='text-align:left;'>Prepared by:</span></p>
      <p class=unstyled style='text-align:left;'><strong>[Sender.Company]</strong></p>
      </div>
  `;

  const componentData = [
    // [emptyContainer()],
    [lineSpace(1)],
    [emptyImage(0)],
    [
      {
        id: uuid.v4(),
        type: DOCUMENT_COMPONENT_TYPES.HTML,
        lock: false,
        label: "Document Title here",
        header: "Document Title here",
        ...emptyTextEditor(mode, headingHTML),
      },
    ],
    [
      {
        id: uuid.v4(),
        type: DOCUMENT_COMPONENT_TYPES.HTML,
        lock: false,
        label: "Sub Title here",
        header: "Sub Title here",
        ...emptyTextEditor(mode, subHeadingHTML),
      },
    ],
    [lineSpace(1)],
    [
      {
        id: uuid.v4(),
        type: DOCUMENT_COMPONENT_TYPES.HTML,
        lock: false,
        label: "Prepared by [Your Name]",
        header: "Prepared by [Your Name]",
        ...emptyTextEditor(mode, clientCompanyHTML),
      },
    ],
  ];

  const { layout, components } = getLayoutAndComponents(componentData);

  const section = {
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.COVERPAGE,
    lock: false,
    label: `Coverpage`,
    header: "",
    data: {
      attributes: {
        background: "#fff",
        colorTransparency: 0.0,
        backgroundImage: "",
        imageTransparency: 1.0,
        appliedTheme: "Default",
        imagePosition: "center right",
        isOpen: true,
      },
      layout,
      components,
    },
  };
  return section;
};

const rawToDraft = data => {
  if (
    DRAFT.DRAFT_RAW_KEYS.length === Object.keys(data).length &&
    JSON.stringify(DRAFT.DRAFT_RAW_KEYS.sort()) ===
      JSON.stringify(Object.keys(data).sort())
  ) {
    return convertFromRaw(data);
  }
  const editorState = EditorState.createEmpty();
  return editorState.getCurrentContent();
};

export const modifyDocumentSection = (section, mode) =>
  convertSectionRawToDraft(section, mode);

export const modifyDocumentContent = (content, mode) => {
  const sections = content.map(section =>
    convertSectionRawToDraft(section.content, mode),
  );
  const updatedContent = {
    sections,
  };
  return updatedContent;
};

export const getDraftContent = (sectionData, mode) => {
  // for cell data with string value
  if (typeof sectionData === "string" || typeof sectionData === "number")
    return sectionData.toString();

  const draftContent = {
    data: sectionData.data,
  };
  if (sectionData.data instanceof EditorState)
    draftContent.data = convertToRaw(sectionData.data.getCurrentContent());
  else if (sectionData.data instanceof ContentState)
    draftContent.data = convertToRaw(sectionData.data);

  if (sectionData.html) draftContent.html = sectionData.html;
  return draftContent;
};

export const getTableDBContent = (tableData, mode) => {
  const draftTableData = {
    ...tableData,
    data: tableData.data.map(row => ({
      ...row,
      data: row.data.map(cell => ({
        ...cell,
        data: getDraftContent(cell.data, mode),
      })),
    })),
  };

  if (draftTableData.attributes) {
    delete draftTableData.attributes.groupSummary;
    delete draftTableData.attributes.groups;
    delete draftTableData.attributes.groupByIndex;
    delete draftTableData.attributes.quoteTableDetails;
    delete draftTableData.attributes.allowModeSelection;
    delete draftTableData.attributes.modesAvailable;
    // delete draftTableData.attributes.firstRowAsHeader;
    delete draftTableData.attributes.isSelectionAllowed;
    // delete draftTableData.attributes.showColorStyles;
    // delete draftTableData.attributes.showColumnOptions;
    // delete draftTableData.attributes.showDataSourceOption;
    delete draftTableData.attributes.showManageColumnOption;
    delete draftTableData.attributes.summary;
    delete draftTableData.attributes.summaryDetails;
  }
  return draftTableData;
};

export const getLayoutData = (layout, components, mode) => {
  const data = [];
  for (let i = 0; i < layout.length; i += 1) {
    if (Array.isArray(layout[i])) {
      const rowData = [];
      for (let j = 0; j < layout[i].length; j += 1) {
        const id = layout[i][j];
        const component = components[id];
        if (component && component.content)
          // eslint-disable-next-line no-use-before-define
          rowData.push(getContentForComponent(component.content, mode));
      }
      data.push(rowData);
    }
  }
  return data;
};

export const getContentForComponent = (section, mode) => {
  let updatedSection = {
    id: section.id,
    type: section.type,
    lock: section.lock,
    data: section.data,
    label: section.label,
    header: section.header,
    clauseID: section.clauseID,
    clauseOptions: section.clauseOptions,
  };
  if (
    section.type === DOCUMENT_COMPONENT_TYPES.HTML ||
    section.type === DOCUMENT_COMPONENT_TYPES.PAGE_NUMBER
  ) {
    // convert data key to raw state
    updatedSection = {
      ...updatedSection,
      ...getDraftContent(section, mode),
    };
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    // convert data key to raw state
    updatedSection = {
      ...updatedSection,
      data: getTableDBContent(section.data, mode),
    };
    const {
      data: {
        attributes: { isLinkedToDataSource },
      },
    } = updatedSection;
    if (isLinkedToDataSource) {
      updatedSection.sheet = section.sheet;
      updatedSection.workSheet = section.workSheet;
      updatedSection.rangeValue = section.rangeValue;
      updatedSection.autoTrim = section.autoTrim;
      updatedSection.connected = section.connected;
      updatedSection.firstRowAsHeader = section.firstRowAsHeader;
    }
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.DEAL_TABLE) {
    updatedSection.showGroupedData = section.showGroupedData;
    updatedSection.showUngroupedData = section.showUngroupedData;
    updatedSection.dealtableView = section.dealtableView;
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    updatedSection.data = {
      ...section.data,
    };
    if (
      section.data.footer &&
      Array.isArray(section.data.footer.layout) &&
      section.data.footer.components
    ) {
      updatedSection.data.footer = {
        ...updatedSection.data.footer,
        data: getLayoutData(
          section.data.footer.layout,
          section.data.footer.components,
          mode,
        ),
      };
      delete updatedSection.data.footer.layout;
      delete updatedSection.data.footer.components;
    }
    if (
      section.data.header &&
      Array.isArray(section.data.header.layout) &&
      section.data.header.components
    ) {
      updatedSection.data.header = {
        ...updatedSection.data.header,
        data: getLayoutData(
          section.data.header.layout,
          section.data.header.components,
          mode,
        ),
      };
      delete updatedSection.data.header.layout;
      delete updatedSection.data.header.components;
    }
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    const componentData = getLayoutData(
      section.data.layout,
      section.data.components,
      mode,
    );
    updatedSection.data = {
      ...section.data,
      data: componentData,
    };
    delete updatedSection.data.layout;
    delete updatedSection.data.components;
  } else if (section.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
    // convert data key to raw state
    updatedSection = {
      ...updatedSection,
      data: getTableDBContent(section.data, mode),
    };
    updatedSection.sheet = section.sheet;
    updatedSection.workSheet = section.workSheet;
    updatedSection.rangeValue = section.rangeValue;
    updatedSection.autoTrim = section.autoTrim;
    updatedSection.connected = section.connected;
    updatedSection.firstRowAsHeader = section.firstRowAsHeader;
  } else if (
    section.type === DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER
  ) {
    updatedSection.justify_content = section.justify_content || "";
  }
  return updatedSection;
};

export const getContentForDB = (sectionList, mode) => {
  // const filteredSections = filterSections(sectionList, mode);
  // const updatedSectionList = filteredSections.map(section =>
  const updatedSectionList = Array.isArray(sectionList)
    ? sectionList.map(section =>
        Array.isArray(section)
          ? getContentForDB(section, mode)
          : getContentForComponent(section, mode),
      )
    : [];
  return updatedSectionList;
};

export const getDealTableColumns = columnDefs => {
  const appFlag = readDataFromLocalStorage("appFlag");
  let dealTableColumns = [];
  if (appFlag && appFlag.deal_columns && Array.isArray(columnDefs)) {
    const configColumns = appFlag.deal_columns.split(",");
    configColumns.forEach(column => {
      const columnDetail = columnDefs.find(col => col.name === column);
      if (columnDetail) {
        dealTableColumns.push({
          text: columnDetail.headerName,
          name: columnDetail.name,
          currency: columnDetail.type === DEALTABLE_COLUMN_TYPES.CurrencyField,
        });
      }
    });
  } else {
    dealTableColumns = DOCUMENTS.DEALTABLE_COLUMNS;
  }
  return dealTableColumns;
};

export const getUngroupedData = (
  metadata,
  rowdata,
  headers,
  columnDefs,
  currency,
  isLinkedQuoteTable,
) => {
  const columns = !headers ? getDealTableColumns(columnDefs) : headers;
  const { fieldMapIndex } = metadata;
  const ungroupedData = rowdata.map(row =>
    columns.map(column => {
      const key = column.name;
      const hashKey = fieldMapIndex[key] || key;
      let cellData = row.rowData[hashKey] ? row.rowData[hashKey].cellData : "";
      if (
        columnDefs &&
        columnDefs.findIndex(col => col.field === hashKey) > -1
      ) {
        const columnDef = columnDefs.find(col => col.field === hashKey);
        if (columnDef.type === DEALTABLE_COLUMN_TYPES.CurrencyField) {
          cellData = numberForamting(cellData, currency);
        } else if (columnDef.type === DEALTABLE_COLUMN_TYPES.DateField) {
          cellData = cellData ? moment(cellData).format("MM/DD/YYYY") : "-";
        } else if (columnDef.type === DEALTABLE_COLUMN_TYPES.PercentField) {
          cellData = cellData
            ? `${Math.round(cellData * 100 + Number.EPSILON) / 100} %`
            : "0.00 %";
        } else if (columnDef.type === DEALTABLE_COLUMN_TYPES.NumericField) {
          cellData = Math.round(cellData * 100 + Number.EPSILON) / 100;
        }
      }

      return isLinkedQuoteTable
        ? { data: cellData, attributes: { id: uuid.v4() } }
        : cellData;
    }),
  );
  return ungroupedData;
};

export const getGroupedData = (metadata, rowdata, discount, headers = []) => {
  const { fieldMapIndex } = metadata;
  const { groups } = discount;
  const groupedData = {};
  groups.forEach(group => {
    const {
      group_by: groupBy,
      group_by_value: groupByValue,
      discountAmount,
      discountedGroupTotal,
      discount_percent: discountPercent,
      group_total: groupTotal,
    } = group;
    const hashKey = fieldMapIndex[groupBy.toLowerCase()];
    const groupRowdata = rowdata.filter(
      row => row.rowData[hashKey].cellData === groupByValue,
    );
    groupedData[groupByValue] = {
      rowData: getUngroupedData(metadata, groupRowdata, headers),
      groupTotal,
      discountAmount,
      discountPercent,
      discountedGroupTotal,
    };
  });
  return groupedData;
};

export const getMergeGroups = (discount, groupSummary) => {
  const { mergeGroupMapping = [] } = groupSummary;

  let changed = false;
  if (discount && discount.groups && Array.isArray(discount.groups)) {
    const groupIDs = discount.groups.map(group => group.id);
    mergeGroupMapping.forEach(group => {
      if (!groupIDs.includes(group.groupID)) {
        changed = true;
      }
    });
  }

  if (changed) {
    return {
      mergeGroupMapping: [],
      mergeGroupMetaData: [],
    };
  }
  return groupSummary;
};

export const getGroupSummaryData = (discount, mergeGroups) => {
  const { groups } = discount;
  const columns = DOCUMENTS.DEALTABLE_GROUPVIEW_HEADERS;
  const { mergeGroupMapping, mergeGroupMetaData } = getMergeGroups(
    discount,
    mergeGroups,
  );
  let unmergedGroupExists = false;

  if (!Array.isArray(mergeGroupMapping) || mergeGroupMapping.length === 0) {
    return {
      data: groups.map(group =>
        [{ category: "unmerged_group", id: group.id }].concat(
          columns.map(header => group[header.key]),
        ),
      ),
      unmergedGroupExists: true,
    };
  }

  const unMergedGroups = groups.filter(
    group =>
      mergeGroupMapping.findIndex(mapping => mapping.groupID === group.id) < 0,
  );
  const groupSummary = unMergedGroups.map(group => {
    unmergedGroupExists = true;
    return [{ category: "unmerged_group", id: group.id }].concat(
      columns.map(header => group[header.key]),
    );
  });

  mergeGroupMetaData.forEach(group => {
    const { id, label, total } = group;

    const groupList = mergeGroupMapping
      .filter(mapping => mapping.mergeGroupID === id)
      .map(mapping => groups.find(g => mapping.groupID === g.id));
    if (groupList.length > 0) {
      // push the merge group header
      groupSummary.push(
        [{ category: "group", id, total }].concat(
          columns.map(header => {
            if (header.key === "group_by_value") {
              return label;
            }
            if (total && header.currency && header.total) {
              let groupTotal = 0;
              groupList.forEach(g => {
                if (g && g[header.key]) {
                  groupTotal += g[header.key];
                }
              });
              return groupTotal;
            }
            return "";
          }),
        ),
      );
      // push group in this merge group
      groupList.forEach(g =>
        groupSummary.push(
          [{ category: "merged_group", id: g.id }].concat(
            columns.map(header => g[header.key]),
          ),
        ),
      );
    }
  });
  return { data: groupSummary, unmergedGroupExists };
};

export const getExportContent = (document, components, event = "preview") => {
  const REQUIRED_SECTIONS_IN_EXPORT = [
    DOCUMENT_COMPONENT_TYPES.COVERPAGE,
    DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER,
  ];

  const {
    created_by: createdBy,
    docNo,
    id: docId,
    objectId,
    name: title,
  } = document;
  const exportSections = Object.keys(components)
    .filter(
      componentId =>
        components[componentId] &&
        components[componentId].content &&
        REQUIRED_SECTIONS_IN_EXPORT.includes(
          components[componentId].content.type,
        ),
    )
    .map(componentId => components[componentId].content);

  const exportContent = {
    content: {
      sections: exportSections,
      info: {
        createdBy,
        docId,
        docNo,
        objectId,
        title,
        appVersion: process.env.REACT_APP_VERSION,
      },
      signatories: document.signatories,
    },
    event,
  };
  return exportContent;
};

export const removeEntity = (data, entityID, accept) => {
  const raw = convertToRaw(data);
  const { blocks, entityMap } = raw;
  const entityKey = Object.keys(entityMap).find(
    key => entityMap[key].data.id === entityID,
  );
  // const entity = entityMap[entityKey];
  const updatedBlocks = [];
  blocks.forEach(block => {
    const { inlineStyleRanges, entityRanges, text } = block;
    const ranges = entityRanges.filter(e => e.key.toString() === entityKey);
    if (!ranges) {
      updatedBlocks.push({
        ...block,
      });
    } else {
      let updatedEntityRanges = entityRanges.filter(
        e => e.key.toString() !== entityKey,
      );
      let updatedInstyleRanges = inlineStyleRanges;
      let updatedText = text;
      if (accept) {
        updatedBlocks.push({
          ...block,
          entityRanges: updatedEntityRanges,
          inlineStyleRanges: updatedInstyleRanges,
        });
      } else {
        ranges.forEach(range => {
          // update the text
          updatedText =
            updatedText.substring(0, range.offset) +
            updatedText.substring(
              range.offset + range.length,
              updatedText.length,
            );

          // get the updated instyle ranges because of remove of text and entity ranges
          updatedInstyleRanges = updatedInstyleRanges.map(instyle => {
            // if the starting point of style is less than starting point of range
            if (instyle.offset < range.offset) {
              if (instyle.offset + instyle.length < range.offset) {
                return instyle;
              }
              if (
                instyle.offset + instyle.length <
                range.offset + range.length
              ) {
                return {
                  ...instyle,
                  length: range.offset - instyle.offset,
                };
              }
              return {
                ...instyle,
                length: instyle.length - range.length,
              };
            }
            if (instyle.offset < range.offset + range.length) {
              if (
                instyle.offset + instyle.length <
                range.offset + range.length
              ) {
                return null;
              }
              return {
                ...instyle,
                offset: range.offset + range.length,
                length:
                  instyle.length -
                  (range.offset + range.length - instyle.offset),
              };
            }
            return {
              ...instyle,
              offset: instyle.offset - (range.offset + range.length),
            };
          });

          // get the updated enity range ranges because of remove of text and entity ranges
          updatedEntityRanges = updatedEntityRanges.map(instyle => {
            if (instyle.offset < range.offset) {
              if (instyle.offset + instyle.length < range.offset) {
                return instyle;
              }
              if (
                instyle.offset + instyle.length <
                range.offset + range.length
              ) {
                return {
                  ...instyle,
                  length: range.offset - instyle.offset,
                };
              }
              return {
                ...instyle,
                length: instyle.length - range.length,
              };
            }
            if (instyle.offset < range.offset + range.length) {
              if (
                instyle.offset + instyle.length <
                range.offset + range.length
              ) {
                return null;
              }
              return {
                ...instyle,
                offset: range.offset + range.length,
                length:
                  instyle.length -
                  (range.offset + range.length - instyle.offset),
              };
            }
            return {
              ...instyle,
              offset: instyle.offset - (range.offset + range.length),
            };
          });
        });
        if (updatedText && updatedText.length > 0) {
          updatedBlocks.push({
            ...block,
            text: updatedText,
            entityRanges: updatedEntityRanges,
            inlineStyleRanges: updatedInstyleRanges,
          });
        }
      }
    }
  });
  delete entityMap[entityKey];
  const updatedRaw = {
    blocks: updatedBlocks,
    entityMap,
  };
  const updatedData = convertFromRaw(updatedRaw);
  return updatedData;
};
const buttonsControlContainer = [
  {
    status: "DRAFT",
    buttons: [
      // {
      //   buttonText: "Preview",
      //   className: "revv-btn-secondary",
      //   functionKey: "showPreviewModal",
      //   controlBy: ["Owner", "Approver", "Follower", "Admin"],
      // },
      // {
      //   buttonText: "Save",
      //   controlBy: ["Owner", "Admin"],
      // },
      {
        buttonText: "Move to Next Stage",
        className: "analyse-document",
        functionKey: "showAnalysisModal",
        controlBy: ["Owner", "Admin"],
      },
    ],
  },
  {
    status: "PENDING_APPROVAL",
    buttons: [
      // {
      //   buttonText: "Preview",
      //   className: "revv-btn-secondary",
      //   functionKey: "showPreviewModal",
      //   controlBy: ["Owner", "Approver", "Follower", "Admin"],
      // },
      {
        buttonText: "Reject",
        className: "reject-document",
        functionKey: "Reject",
        controlBy: ["Approver"],
      },
      {
        buttonText: "Approve",
        className: "approve-document",
        functionKey: "Approve",
        controlBy: ["Approver"],
      },
    ],
  },
  {
    status: "REJECTED",
    buttons: [
      // {
      //   buttonText: "Preview",
      //   className: "revv-btn-secondary",
      //   functionKey: "showPreviewModal",
      //   controlBy: ["Owner", "Approver", "Follower", "Admin"],
      // },
      {
        buttonText: "EditBtnWithRbac",
        functionKey: "showConfirmMoveToDraftPopup",
        controlBy: ["Owner", "Admin"],
      },
    ],
  },
  {
    status: "APPROVED",
    buttons: [
      // {
      //   buttonText: "Preview",
      //   className: "revv-btn-secondary",
      //   functionKey: "showPreviewModal",
      //   controlBy: ["Owner", "Approver", "Follower", "Admin"],
      // },
      {
        buttonText: "EditBtnWithRbac",
        functionKey: "showConfirmMoveToDraftPopup",
        controlBy: ["Owner", "Admin"],
      },
      {
        buttonText: "Upload Revision",
        className: "revv-btn-secondary",
        functionKey: "Upload",
        controlBy: ["Owner", "Admin"],
      },
      {
        buttonText: "Send for Signature",
        className: "signature-btn",
        functionKey: "showSignatureMoveModal",
        controlBy: ["Owner", "Admin"],
      },
      {
        buttonText: "Share",
        className: "revv-btn-secondary",
        functionKey: "showShareDocumentPopup",
        controlBy: ["Owner", "Admin"],
      },
      {
        buttonText: "ExportDocumentBtn",
        controlBy: ["Owner", "Approver", "Admin"],
      },
    ],
  },
  {
    status: "PREPARE_FOR_SIGNATURE",
    buttons: [
      {
        buttonText: "CancleESignature",
        functionKey: "cancleESignature",
        className: "revv-btn-secondary",
        controlBy: ["Owner", "Admin"],
      },
      {
        buttonText: "Send",
        functionKey: "sendForSignature",
        controlBy: ["Owner", "Admin"],
      },
    ],
  },
  {
    status: "SIGNATURE_PENDING",
    buttons: [
      // {
      //   buttonText: "Preview",
      //   className: "revv-btn-secondary",
      //   functionKey: "showPreviewModal",
      //   controlBy: ["Owner", "Approver", "Follower", "Admin"],
      // },
      {
        buttonText: "ExportDocumentBtn",
        controlBy: ["Owner", "Approver", "Admin"],
      },
    ],
  },

  {
    status: "DOCUMENT_CLIENT_SIGNED",
    buttons: [
      {
        buttonText: "Download",
        className: "revv-btn-secondary",
        functionKey: "download",
        controlBy: ["Owner", "Approver", "Admin", "Teammate"],
      },
    ],
  },
];

export const displayButtons = (
  docStatus,
  docOwnerID,
  docPendingApprovers,
  pendingApprovalRequired,
  docAllApprovers,
  scope,
  // docApprovalStatus,
) => {
  let data = [];
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  let userStatus;

  if (
    CURRENT_USER.id === docOwnerID &&
    (CURRENT_USER.roles[0].name !== "SalesOps" ||
      CURRENT_USER.roles[0].name !== USER_ROLES.ADMIN) &&
    docPendingApprovers.includes(CURRENT_USER.id)
  ) {
    userStatus = "Owner";
  } else if (
    (CURRENT_USER.roles[0].name === "SalesOps" ||
      CURRENT_USER.roles[0].name === USER_ROLES.ADMIN) &&
    !docPendingApprovers.includes(CURRENT_USER.id)
  ) {
    userStatus = "Admin";
  } else if (docPendingApprovers.includes(CURRENT_USER.id)) {
    userStatus = "Approver";
  } else {
    // eslint-disable-next-line no-lonely-if
    if (
      CURRENT_USER.id === docOwnerID &&
      CURRENT_USER.roles[0].name !== "SalesOps" &&
      !docPendingApprovers.includes(CURRENT_USER.id)
    ) {
      userStatus = "Owner";
    } else if (
      CURRENT_USER.roles[0].name === "SalesOps" &&
      !docPendingApprovers.includes(CURRENT_USER.id)
    ) {
      userStatus = "Admin";
    } else if (
      (docAllApprovers && docAllApprovers.includes(CURRENT_USER.id)) ||
      docPendingApprovers.includes(CURRENT_USER.id)
    ) {
      userStatus = "Approver";
    } else if (scope.write || scope.read) {
      userStatus = "Teammate";
    } else {
      userStatus = "Follower";
    }
  }
  // eslint-disable-next-line no-plusplus
  for (let v = 0; v < buttonsControlContainer.length; v++) {
    if (buttonsControlContainer[v].status === docStatus) {
      if (docStatus === "PENDING_APPROVAL") {
        if (pendingApprovalRequired) {
          data = buttonsControlContainer[v].buttons;
        } else {
          data = [];
        }
      } else if (
        docStatus === "REJECTED" &&
        ((docPendingApprovers.includes(docOwnerID) &&
          CURRENT_USER.id === docOwnerID) ||
          buttonsControlContainer[v].buttons[0].controlBy.includes(userStatus))
      ) {
        data = buttonsControlContainer[v].buttons;
      } else {
        // eslint-disable-next-line no-plusplus
        for (let b = 0; b < buttonsControlContainer[v].buttons.length; b++) {
          if (
            buttonsControlContainer[v].buttons[b].controlBy.includes(userStatus)
          ) {
            data.push(buttonsControlContainer[v].buttons[b]);
          }
        }
      }
    }
  }
  return data;
};

export const displayDropdown = (docOwnerID, docPendingApprovers) => {
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  let userStatus;
  if (
    CURRENT_USER.id === docOwnerID &&
    CURRENT_USER.roles[0].name !== USER_ROLES.ADMIN &&
    !docPendingApprovers.includes(CURRENT_USER.id)
  ) {
    userStatus = "Owner";
  } else if (
    CURRENT_USER.roles[0].name === USER_ROLES.ADMIN &&
    !docPendingApprovers.includes(CURRENT_USER.id)
  ) {
    userStatus = "Admin";
  } else if (docPendingApprovers.includes(CURRENT_USER.id)) {
    userStatus = "Approver";
  } else {
    userStatus = "Follower";
  }
  return userStatus;
};

// get disable status for options in status change dropdown
export const getDisableStatus = (status, lane) => {
  if (status === "DRAFT" && lane.status === "DRAFT") {
    return true;
  }
  if (
    status === "APPROVED" &&
    (lane.status === "DOCUMENT_CLIENT_REJECTED" ||
      lane.status === "SENT_TO_CLIENT")
  ) {
    return false;
  }
  if (
    status === "SENT_TO_CLIENT" &&
    (lane.status === "DOCUMENT_CLIENT_APPROVED" ||
      lane.status === "DOCUMENT_CLIENT_REJECTED")
  ) {
    return false;
  }
  return true;
};
// get disable color for options in status change dropdown
export const getStyleProps = (status, lane) => {
  if (
    (status === "DRAFT" && lane.status === "DRAFT") ||
    (status === "PENDING_APPROVAL" && lane.status === "PENDING_APPROVAL") ||
    (status === "REJECTED" && lane.status === "REJECTED") ||
    (status === "DOCUMENT_CLIENT_APPROVED" &&
      lane.status === "DOCUMENT_CLIENT_APPROVED") ||
    (status === "DOCUMENT_CLIENT_REJECTED" &&
      lane.status === "DOCUMENT_CLIENT_REJECTED") ||
    (status === "DOCUMENT_CLIENT_SIGNED" &&
      lane.status === "DOCUMENT_CLIENT_SIGNED")
  ) {
    return {
      background: "",
    };
  }
  if (
    status === "APPROVED" &&
    (lane.status === "APPROVED" ||
      lane.status === "DOCUMENT_CLIENT_REJECTED" ||
      lane.status === "SENT_TO_CLIENT")
  ) {
    return { background: "" };
  }
  if (
    status === "SENT_TO_CLIENT" &&
    (lane.status === "SENT_TO_CLIENT" ||
      lane.status === "DOCUMENT_CLIENT_APPROVED" ||
      lane.status === "DOCUMENT_CLIENT_REJECTED")
  ) {
    return { background: "" };
  }
  return { background: "#dddddd" };
};

export const checkAcceptRejectNotes = documentNotes => {
  const unVisitedNotes =
    Array.isArray(documentNotes) &&
    documentNotes.filter(note => {
      const { anchor, comment_status: commentStatus } = note;
      if (
        anchor &&
        (anchor.action === "DELETED" || anchor.action === "INSERTED") &&
        commentStatus !== "Accepted" &&
        commentStatus !== "Rejected"
      ) {
        return true;
      }
      return false;
    });
  const acceptRejectChanges = !!(
    Array.isArray(unVisitedNotes) && unVisitedNotes.length > 0
  );
  return acceptRejectChanges;
};

export const convertIntermediateJson = res => {
  delete res.FieldTypes.CLAUSE;
  const obj = res.FieldTypes;
  let data = [];
  const mapKnownLabelTransforms = {
    US_STATES: "US States",
    MULTI_LINE_TEXT: "Multi-line",
    PERSON_NAME: "Name of the person",
    PERCENT: "Percentage",
    TOGGLE: "Checkbox (only)",
  };
  data = Object.keys(obj).map((key, index) => {
    if (key in mapKnownLabelTransforms) {
      return Object.assign({
        name: key,
        label: mapKnownLabelTransforms[key],
        id: index,
        props: obj[key],
      });
    }
    return Object.assign({
      name: key,
      label: key
        .replace(
          /\w+/g,
          txt =>
            // uppercase first letter and add rest Lowercase
            txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
        )
        .replace(/_/g, " "),
      id: index,
      props: obj[key],
    });
  });
  return data;
};

export const updateButtonStatus = data => {
  let status = false;
  if (
    data.name !== undefined &&
    (data.name === "EMAIL" || data.name === "URL")
  ) {
    if (data.fields !== undefined) {
      data.fields.forEach(d => {
        if (d.value === "" && d.type !== "checkbox") {
          status = true;
        } else if (
          d.value !== "" &&
          d.id === "DEFAULT VALUE" &&
          d.notValidText !== "" &&
          d.type !== "checkbox"
        ) {
          status = true;
        }
      });
    } else {
      status = true;
    }
  } else if (data.fields !== undefined) {
    data.fields.forEach(d => {
      if (
        (d.value === "" || d.value === []) &&
        d.type !== "checkbox" &&
        d.required === true
      ) {
        status = true;
      }
    });
  } else {
    status = true;
  }
  return status;
};

export const _urlIsValid = value => {
  // eslint-disable-next-line no-useless-escape
  const re =
    /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}[.]{0,1}/;
  return re.test(value);
};

export const _emailIsValid = value => {
  // eslint-disable-next-line no-useless-escape
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(value).toLowerCase());
};

export const getUniqueWorkflowApprovers = analysis => {
  let uniqueApproversCount = 0;
  const uniqueApprovers = [];
  if (analysis.length === 0) {
    return { uniqueApprovers, uniqueApproversCount };
  }
  analysis.forEach(item => {
    item.approvers.forEach(approver => {
      if (uniqueApprovers.length === 0) {
        uniqueApprovers.push(approver);
        uniqueApproversCount = 1;
      }
      if (
        uniqueApprovers.find(unqApprover => approver.id === unqApprover.id) ===
        undefined
      ) {
        uniqueApprovers.push(approver);
        uniqueApproversCount += 1;
      }
    });
  });
  return { uniqueApprovers, uniqueApproversCount };
};

export const handleHyperlink = (editorState, url) => {
  const entityKey = Entity.create("LINK", "MUTABLE", {
    target: "_blank",
    url,
    id: uuid.v4(),
    selectedText: getSelectedText(editorState),
  });
  const newState = RichUtils.toggleLink(
    editorState,
    editorState.getSelection(),
    entityKey,
  );
  return newState;
};

export const getClauseOptionValue = value => {
  let data = ContentState.createFromText("");
  let html = BLANK_HTML;
  if (typeof value === "string") {
    if (value.startsWith("<")) {
      html = value;
      data = HTMLtoDraft(value);
    } else if (value.startsWith("{")) {
      const rawData = JSON.parse(value);
      data = convertFromRaw(rawData);
      html = getEditorHTML(data);
    }
  }
  return { data, html };
};

export const getClauseOptionView = (value, mode) => {
  if (typeof value === "string") {
    if (value.startsWith("<")) return value;
    if (value.startsWith("{")) {
      const rawData = JSON.parse(value);
      const data = convertFromRaw(rawData);
      return getEditorHTML(data, mode);
    }
  }
  return null;
};

export const getClauseOptionData = value => {
  let data = convertToRaw(ContentState.createFromText(""));
  if (typeof value === "string") {
    if (value.startsWith("<")) {
      data = convertToRaw(HTMLtoDraft(value));
    } else if (value.startsWith("{")) {
      data = JSON.parse(value);
    }
  }
  return data;
};

export const replaceClauseOptions = (
  components,
  fieldsUsed,
  updatedComponents = [],
) => {
  function _updateContentData(component, data) {
    component.content.data = data;
    component.content.clauseOptions = null;
    component.content.form_field = [];
    component.content.id = component.component_id;
    updatedComponents.push(component.content);
  }

  //* Replace ClauseOptions With Default Value
  components.forEach(component => {
    if (
      component.content &&
      component.content.clauseOptions &&
      component.content.type === DOCUMENT_COMPONENT_TYPES.HTML
    ) {
      const { id, value } = component.content.clauseOptions;
      if (fieldsUsed[id]) {
        // Handle FreeText/SingleSelect clause type without default value
        if (!value) {
          _updateContentData(component, getClauseOptionData([]));
          updatedComponents[updatedComponents.length - 1].html = BLANK_HTML;
        }
        // Handle FreeText/SingleSelect clause type with default value
        else if (typeof value === "string") {
          const { default: defaultValue = "", subtype } = fieldsUsed[id].meta;
          if (subtype === "free_text") {
            _updateContentData(component, getClauseOptionData(defaultValue));
          } else {
            const defaultClause = fieldsUsed[id].meta.clauses.find(
              clause => clause.id === defaultValue,
            ).value;
            _updateContentData(component, getClauseOptionData(defaultClause));
          }
        }
        // Handle Optional/MultiSelect clause type without default value
        else if (Array.isArray(value) && value.length === 0) {
          _updateContentData(component, getClauseOptionData([]));
          updatedComponents[updatedComponents.length - 1].html = BLANK_HTML;
        }
        // Handle Optional/MultiSelect clause type with default value
        else if (Array.isArray(value) && value.length > 0) {
          const defaultClauseIds = fieldsUsed[id].meta.default;
          const appendedClauseData = { blocks: [], entityMap: {} };
          defaultClauseIds.forEach(clauseId => {
            const clauseValue = fieldsUsed[id].meta.clauses.find(
              clause => clause.id === clauseId,
            ).value;
            const rawData = getClauseOptionData(clauseValue);
            appendedClauseData.blocks.push(...rawData.blocks);
          });
          _updateContentData(component, appendedClauseData);
        }
      }
    }
  });

  return updatedComponents;
};

export const replaceDynamicFieldsInBlockData = (
  blockData,
  formFields,
  fieldsUsed,
) => {
  const { blocks, entityMap } = blockData;
  let isComponentUpdated = false;
  const updatedBlocks = blocks.map(block => {
    let isDynamicFiledExists = false;
    let deltaTextLength = 0;
    let updatedText = block.text;
    const { inlineStyleRanges = [], entityRanges = [] } = block;
    const entitiesTobeReplaced = [];
    let updatedEntityRanges = [...entityRanges];
    let updatedInstyleRanges;
    // get dynamic field's value to be replaced and others
    entityRanges.forEach(entity => {
      let flag = false;
      let fieldTextValue;
      const entityValue = entityMap[entity.key];
      if (entityValue.type === "DYNAMICFIELD") {
        flag = true;
        // if it is dynamic field and it is a doc creation from editor based template
        // and if that has default value, then leave this dynamic field as it is.
        const { data = {} } = entityValue;
        const { dynamicFieldId } = data;
        const field = fieldsUsed[dynamicFieldId];
        // Filter the current dynamic field
        const formField = formFields.find(
          f => f.dynamicFieldId === dynamicFieldId,
        );
        if (formField && formField.value) {
          field.value = formField.value;
          fieldTextValue = getFieldValue(field);
        } else if (field.value) {
          fieldTextValue = getFieldValue(field);
        } else {
          flag = false;
        }
      }
      if (flag) {
        entitiesTobeReplaced.push({ ...entity, textToReplace: fieldTextValue });
      }
    });
    entitiesTobeReplaced.forEach(entity => {
      // Declare needed keys
      const { key, length, offset, textToReplace } = entity;
      const styles = [];
      const entities = [];

      if (textToReplace) {
        // replace field text with dynamic field name
        updatedText =
          updatedText.substring(0, offset + deltaTextLength) +
          textToReplace +
          updatedText.substring(
            offset + length + deltaTextLength,
            updatedText.length - 1,
          );

        // now update all the ranges after this entity field
        inlineStyleRanges.forEach(style => {
          let rangeStyle = style.style;
          const { offset: styleOffset, length: styleLength } = style;
          if (rangeStyle > styleOffset + styleLength) {
            rangeStyle = rangeStyle - styleLength + textToReplace.length;
          }
          styles.push({
            offset: styleOffset,
            length: styleLength,
            style: rangeStyle,
          });
        });

        updatedEntityRanges.forEach(entityRange => {
          const { key: entityKey } = entityRange;
          let { offset: entityOffset, length: entityLength } = entityRange;
          if (key === entityKey) {
            entityLength = textToReplace.length;
          } else if (entityOffset >= offset + length) {
            entityOffset = entityOffset - length + textToReplace.length;
          }
          entities.push({
            offset: entityOffset,
            length: entityLength,
            key: entityKey,
          });
        });

        deltaTextLength = deltaTextLength + textToReplace.length - length;
        updatedEntityRanges = entities;
        updatedInstyleRanges = styles;
        isDynamicFiledExists = true;
      }
    });

    if (isDynamicFiledExists) {
      isComponentUpdated = true;
      block.text = updatedText;
      block.entityRanges = updatedEntityRanges;
      block.inlineStyleRanges = updatedInstyleRanges || inlineStyleRanges;
    }
    return block;
  });
  return {
    updatedData: {
      blocks: updatedBlocks,
      entityMap,
    },
    isComponentUpdated,
  };
};

export const replaceDynamicFields = (
  components,
  fieldsUsed,
  updatedComponents = [],
) => {
  components.forEach(component => {
    let isUpdated = false;
    const { content } = component;
    const { type, data: contentData, form_field: formField } = content;
    if (Array.isArray(formField) && formField.length > 0) {
      if (type === DOCUMENT_COMPONENT_TYPES.HTML) {
        const { updatedData, isComponentUpdated } =
          replaceDynamicFieldsInBlockData(contentData, formField, fieldsUsed);
        isUpdated = isUpdated || isComponentUpdated;
        content.data = updatedData;
      } else if (type === DOCUMENT_COMPONENT_TYPES.TABLE) {
        const { data, attributes } = contentData;
        const updatedData = data.map(row => {
          const { data: rowData, attributes: rowAttributes } = row;
          const updatedRowData = rowData.map(cell => {
            const { data: columnData, attributes: cellAttributes } = cell;
            if (
              !(
                columnData &&
                "data" in columnData &&
                "blocks" in columnData.data &&
                "entityMap" in columnData.data
              )
            ) {
              return cell;
            }
            const { updatedData: updatedCellData, isComponentUpdated } =
              replaceDynamicFieldsInBlockData(
                columnData.data,
                formField,
                fieldsUsed,
              );
            isUpdated = isUpdated || isComponentUpdated;
            columnData.data = updatedCellData;
            return {
              data: columnData,
              attributes: cellAttributes,
            };
          });
          return {
            data: updatedRowData,
            attributes: rowAttributes,
          };
        });
        content.data = {
          data: updatedData,
          attributes,
        };
      } else if (type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
        const { header, footer } = contentData;
        if (header) {
          const { data: headerData } = header;
          const compData = headerData.map(d => ({
            component_id: d.section_id,
            content: d,
          }));
          const updatedComp = replaceDynamicFields(compData, fieldsUsed, []);
          header.data =
            updatedComp.length > 0
              ? headerData.map(d => {
                  const inUpdatedList = updatedComp.find(
                    uc => uc.component_id === d.section_id,
                  );
                  if (inUpdatedList) {
                    d.data = isUpdated.data;
                  }
                  return d;
                })
              : headerData;
          isUpdated = isUpdated || updatedComp.length > 0;
        }
        if (footer) {
          const { data: footerData } = footer;
          const compData = footerData.map(d => ({
            component_id: d.section_id,
            content: d,
          }));
          const updatedComp = replaceDynamicFields(compData, fieldsUsed, []);
          footer.data =
            updatedComp.length > 0
              ? footerData.map(d => {
                  const inUpdatedList = updatedComp.find(
                    uc => uc.component_id === d.section_id,
                  );
                  if (inUpdatedList) {
                    d.data = isUpdated.data;
                  }
                  return d;
                })
              : footerData;
          isUpdated = isUpdated || updatedComp.length > 0;
        }
        content.data = {
          ...contentData,
          header,
          footer,
        };
      } else if (type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
        const compData = contentData.data.map(d => ({
          component_id: d.section_id,
          content: d,
        }));
        const updatedComp = replaceDynamicFields(compData, fieldsUsed, []);
        content.data.data =
          updatedComp.length > 0
            ? contentData.map(d => {
                const inUpdatedList = updatedComp.find(
                  uc => uc.component_id === d.section_id,
                );
                if (inUpdatedList) {
                  d.data = isUpdated.data;
                }
                return d;
              })
            : contentData;
        isUpdated = isUpdated || updatedComp.length > 0;
      }
      // Push only if the component has dynamic filed
      if (isUpdated) {
        content.form_field = [];
        updatedComponents.push(content);
      }
    }
  });
  return updatedComponents;
};

export const replaceClauseAndDynamicFields = (components, fieldsUsed) => {
  const componentsClone = JSON.parse(JSON.stringify(components));
  const fieldsUsedMap = {};
  const updatedComponents = [];
  //* Create HashMap for FieldsUsed [{}]
  fieldsUsed.forEach(field => {
    fieldsUsedMap[field.id] = field;
  });
  replaceClauseOptions(componentsClone, fieldsUsedMap, updatedComponents);
  replaceDynamicFields(componentsClone, fieldsUsedMap, updatedComponents);
  return updatedComponents;
};

export const getComponentPosition = (sectionID, layout) => {
  for (let i = 0; i < layout.length; i += 1) {
    for (let j = 0; j < layout[i].length; j += 1) {
      if (layout[i][j] === sectionID) return { colIndex: j, rowIndex: i };
    }
  }
  return {
    rowIndex: undefined,
    colIndex: undefined,
  };
};

export const getComponentFromInfo = (components, componentMeta) => {
  if (!(componentMeta && components)) return null;

  const { componentId, blockType } = componentMeta;
  let component;
  if (blockType === DOCUMENT_BLOCKS.BODY) {
    component = components[componentId];
  }
  if (blockType === DOCUMENT_BLOCKS.COVERPAGE && componentMeta.coverpageId) {
    const { coverpageId } = componentMeta;
    component =
      components[coverpageId] &&
      components[coverpageId].content &&
      components[coverpageId].content.data
        ? components[coverpageId].content.data.components[componentId]
        : null;
  }
  if (blockType === DOCUMENT_BLOCKS.HEADER && componentMeta.headerId) {
    const { headerId } = componentMeta;
    component =
      components[headerId] &&
      components[headerId].content &&
      components[headerId].content.data &&
      components[headerId].content.data.header
        ? components[headerId].content.data.header.components[componentId]
        : null;
  }
  if (blockType === DOCUMENT_BLOCKS.FOOTER && componentMeta.footerId) {
    const { footerId } = componentMeta;
    component =
      components[footerId] &&
      components[footerId].content &&
      components[footerId].content.data &&
      components[footerId].content.data.footer
        ? components[footerId].content.data.footer.components[componentId]
        : null;
  }

  if (!component) return null;

  return component.content;
};

export const getUpdatedParentComponent = (
  components,
  componentMeta,
  newSectionData,
) => {
  let updatedSection;
  const { componentId, componentType, blockType } = componentMeta;
  let section;
  let newSection;

  // get the section to be updated
  if (blockType === DOCUMENT_BLOCKS.BODY) {
    section = components[componentId].content;
  } else if (blockType === DOCUMENT_BLOCKS.HEADER && componentMeta.headerId) {
    section =
      components[componentMeta.headerId].content.data.header.components[
        componentId
      ].content;
  } else if (blockType === DOCUMENT_BLOCKS.FOOTER && componentMeta.footerId) {
    section =
      components[componentMeta.footerId].content.data.footer.components[
        componentId
      ].content;
  } else if (
    blockType === DOCUMENT_BLOCKS.COVERPAGE &&
    componentMeta.coverpageId
  ) {
    section =
      components[componentMeta.coverpageId].content.data.components[componentId]
        .content;
  }

  // get update according to section type
  if (componentType === DOCUMENT_COMPONENT_TYPES.HTML) {
    newSection = { ...newSectionData };
  } else if (componentType === DOCUMENT_COMPONENT_TYPES.TABLE) {
    const { tableRowId, tableColId } = componentMeta;
    newSection = {
      ...section,
    };
    newSection.data.data[tableRowId].data[tableColId].data = newSectionData;
  }

  // update the section now
  if (blockType === DOCUMENT_BLOCKS.BODY) {
    updatedSection = { ...newSection };
  } else if (blockType === DOCUMENT_BLOCKS.HEADER && componentMeta.headerId) {
    const { headerId } = componentMeta;
    updatedSection = {
      ...components[headerId].content,
      data: {
        ...components[headerId].content.data,
        header: {
          ...components[headerId].content.data.header,
          components: {
            ...components[headerId].content.data.header.components,
            [componentId]: {
              content: newSection,
            },
          },
        },
      },
    };
  } else if (blockType === DOCUMENT_BLOCKS.FOOTER && componentMeta.footerId) {
    const { footerId } = componentMeta;
    updatedSection = {
      ...components[footerId].content,
      data: {
        ...components[footerId].content.data,
        footer: {
          ...components[footerId].content.data.footer,
          components: {
            ...components[footerId].content.data.footer.components,
            [componentId]: {
              content: newSection,
            },
          },
        },
      },
    };
  } else if (
    blockType === DOCUMENT_BLOCKS.COVERPAGE &&
    componentMeta.coverpageId
  ) {
    const { coverpageId } = componentMeta;
    updatedSection = {
      ...components[coverpageId].content,
      data: {
        ...components[coverpageId].content.data,
        components: {
          ...components[coverpageId].content.data.components,
          [componentId]: {
            content: newSection,
          },
        },
      },
    };
  }
  return updatedSection;
};

export const getEditorData = (component, componentMeta) => {
  // const component = getComponentFromInfo(components, componentMeta);
  if (!component) return null;

  if (component) {
    const { componentType } = componentMeta;
    if (componentType === DOCUMENT_COMPONENT_TYPES.HTML) {
      return component;
    }
    if (componentType === DOCUMENT_COMPONENT_TYPES.TABLE) {
      const { data: tableData } = component;
      const { tableRowId, tableColId } = componentMeta;
      return tableData.data[tableRowId].data[tableColId].data;
    }
  }
  return null;
};

export const getComponentEditorState = (component, componentMeta) => {
  if (!component) return null;
  const { componentType } = componentMeta;
  if (
    componentType === DOCUMENT_COMPONENT_TYPES.HTML ||
    componentType === DOCUMENT_COMPONENT_TYPES.PAGE_NUMBER
  ) {
    return component.editorState;
  }
  if (
    componentType === DOCUMENT_COMPONENT_TYPES.TABLE ||
    componentType === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET
  ) {
    const { data: tableData } = component;
    const { tableRowId, tableColId } = componentMeta;
    if (
      tableData.data[tableRowId] &&
      tableData.data[tableRowId].data &&
      tableData.data[tableRowId].data[tableColId] &&
      tableData.data[tableRowId].data[tableColId].data
    )
      return tableData.data[tableRowId].data[tableColId].data.editorState;
  }
  return null;
};

export const isEditorActive = (componentMeta, activeComponentMeta) => {
  const { componentType, componentId } = componentMeta;
  const { componentId: activeComponentId } = activeComponentMeta;

  if (componentId === activeComponentId) {
    if (componentType === DOCUMENT_COMPONENT_TYPES.HTML) {
      return true;
    }
    if (componentType === DOCUMENT_COMPONENT_TYPES.TABLE) {
      const { tableRowId, tableColId } = componentMeta;
      const { tableRowId: activeTableRowId, tableColId: activeTableColId } =
        activeComponentMeta;
      if (tableRowId === activeTableRowId && tableColId === activeTableColId) {
        return true;
      }
    }
  }
  return false;
};

export const convertDocumentContent = (content, mode, fieldsUsed) => {
  const sections = content.map(section => {
    const comp = getComponentStructure(section);
    return convertSectionRawToDraft(comp.content, mode, fieldsUsed);
  });
  const updatedContent = {
    sections,
  };
  return updatedContent;
};

export const sanitizeHTML = dirtyHTML =>
  sanitizeHtml(dirtyHTML, {
    allowedTags: [
      "div",
      "p",
      "span",
      "ol",
      "ul",
      "li",
      "b",
      "i",
      "u",
      "strong",
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      "a",
      "em",
      "br",
    ],
    selfClosing: ["br", "hr", "meta"],
    allowedAttributes: {
      "*": ["charset", "href", "target", "style", "class", "classname"],
    },
  });
export const convertSignersData = (data, signersSignatories = null) => {
  const updatedSignersData = [];
  let i = 0;
  data.forEach(signer => {
    const firstName = signer.firstName || signer.first_name;
    const lastName = signer.lastName || signer.last_name;
    const signerEmail = signer.signerEmail || signer.signer_email;
    const options =
      signer.signerConfig && Array.isArray(signer.signerConfig)
        ? signer.signerConfig
        : signer.signer_config.options || [];
    const imageUrl =
      signer.imageUrl || signer.signer_config
        ? signer.signer_config.image_url
        : "";
    const point =
      signer && signer.signer_config && signer.signer_config.point
        ? signer.signer_config.point
        : {};
    const datePoint =
      signer && signer.signer_config && signer.signer_config.date_point
        ? signer.signer_config.date_point
        : null;
    const namePoint =
      signer && signer.signer_config && signer.signer_config.name_point
        ? signer.signer_config.name_point
        : null;
    const initialsPoint =
      signer && signer.signer_config && signer.signer_config.initials_point
        ? signer.signer_config.initials_point
        : null;
    const signatureType =
      signer && signer.signer_config && signer.signer_config.signature_type
        ? signer.signer_config.signature_type
        : "FULLNAME";
    const newSignerData = {
      ...signer,
      id:
        signersSignatories && signersSignatories.length > i
          ? signersSignatories[i].id
          : signer.id,
      first_name: firstName,
      last_name: lastName,
      signer_email: signerEmail,
      signer_config: {
        image_url: imageUrl,
        options,
        point,
        signature_type: signatureType,
      },
      signerConfig: undefined,
      firstName: undefined,
      lastName: undefined,
      signerEmail: undefined,
    };
    i += 1;
    // dont add keys if not present
    if (datePoint) newSignerData.signer_config.date_point = datePoint;
    if (namePoint) newSignerData.signer_config.name_point = namePoint;
    if (initialsPoint)
      newSignerData.signer_config.initials_point = initialsPoint;
    updatedSignersData.push(newSignerData);
  });
  return updatedSignersData;
};

export const removeEntities = data => {
  const entitiesToBeRemoved = [
    "DYNAMICFIELD",
    "NOTES",
    "COMMENTS",
    "DELETED",
    "INSERTED",
  ];
  const indexesToBeRemoved = [];
  if (Object.keys(data.entityMap).length > 0) {
    let updatedEntityMap = {};
    Object.keys(data.entityMap).forEach(key => {
      if (!entitiesToBeRemoved.includes(data.entityMap[key].type)) {
        updatedEntityMap = {
          ...updatedEntityMap,
          [key]: { ...data.entityMap[key] },
        };
      } else {
        indexesToBeRemoved.push(parseInt(key, 10));
      }
    });
    data.entityMap = { ...updatedEntityMap };

    data.blocks.forEach(block => {
      let updatedEntityRanges = [];
      block.entityRanges.forEach(range => {
        if (!indexesToBeRemoved.includes(range.key)) {
          updatedEntityRanges = [...updatedEntityRanges, { ...range }];
        }
      });
      block.entityRanges = [...updatedEntityRanges];
    });
  }
  return data;
};

export const convertContentData = componentContent => {
  let editorData;
  if (!componentContent || !componentContent.data) {
    return componentContent;
  }
  if (componentContent.currentContent) {
    editorData = componentContent.currentContent;
    delete componentContent.currentContent;
  } else {
    editorData = componentContent.data;
  }
  if (editorData && editorData.blocks && editorData.entityMap) {
    editorData = removeEntities(editorData);
  }
  componentContent.data = { ...editorData };
  return componentContent;
};

export const updateComponentList = componentList => {
  if (!Array.isArray(componentList)) {
    return componentList;
  }
  const updatedList = componentList.map(component => {
    if (Array.isArray(component)) return updateComponentList(component);

    // suppose its not a list
    // eslint-disable-next-line no-use-before-define
    return updateContentData(component.type, component);
  });
  return updatedList;
};

export const updateContentData = (componentType, componentContent) => {
  switch (componentType) {
    case "HTML":
      return convertContentData(componentContent);
    case "Table":
      if (
        componentContent.data.attributes &&
        componentContent.data.attributes.dataSource
      ) {
        componentContent.data = emptyTableData(DOCUMENT_MODE.TEMPLATE, 2);
      } else {
        componentContent.data.data.forEach(row => {
          if (Array.isArray(row.data) && row.data.length > 0) {
            row.data.forEach(cell => {
              cell.data = convertContentData(cell.data);
            });
          }
        });
      }

      if (componentContent.sheet) {
        delete componentContent.sheet;
        delete componentContent.workSheet;
        delete componentContent.rangeValue;
        delete componentContent.autoTrim;
      }

      return componentContent;
    case "HeaderFooter":
      componentContent.data.header.data = updateComponentList(
        componentContent.data.header.data,
      );
      componentContent.data.footer.data = updateComponentList(
        componentContent.data.footer.data,
      );
      return componentContent;
    case "CoverPage":
      componentContent.data.data = updateComponentList(
        componentContent.data.data,
      );
      return componentContent;
    default:
      return componentContent;
  }
};

export const updateComponentDataForTemplates = components => {
  const updatedComponents = [];
  const typesToConvert = ["HTML", "Table", "HeaderFooter", "CoverPage"];
  components.forEach(component => {
    if (Array.isArray(component) && component.length > 0) {
      component.forEach(comp => {
        if (typesToConvert.includes(comp.type)) {
          comp.content = updateContentData(comp.type, comp.content);
          updatedComponents.push({
            component_id: comp.component_id,
            content: { ...comp.content },
          });
        }
      });
    } else if (component.content && typesToConvert.includes(component.type)) {
      component.content = updateContentData(component.type, component.content);
      updatedComponents.push({
        component_id: component.component_id,
        content: { ...component.content },
      });
    }
  });
  return updatedComponents;
};

export const isComponentCloneable = type =>
  type === DOCUMENT_COMPONENT_TYPES.HTML ||
  type === DOCUMENT_COMPONENT_TYPES.TABLE ||
  type === DOCUMENT_COMPONENT_TYPES.IMAGE ||
  type === DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER ||
  type === DOCUMENT_COMPONENT_TYPES.STRIPE_PAYMENT_BUTTON ||
  type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET ||
  type === DOCUMENT_COMPONENT_TYPES.PAGE_BREAK ||
  type === DOCUMENT_COMPONENT_TYPES.LINE_SPACE;

export const cloneTable = oldSection => {
  if (
    !oldSection ||
    !oldSection.data ||
    !oldSection.data.data ||
    !Array.isArray(oldSection.data.data)
  ) {
    return null;
  }

  const rows = [];
  oldSection.data.data.forEach(row => {
    let newRow;
    if (
      oldSection.data.attributes.dataSource === "DealTable" &&
      oldSection.data.attributes.connected
    ) {
      newRow = {
        attributes: { ...row.attributes },
        data: [],
      };
    } else {
      newRow = {
        attributes: { ...row.attributes, id: uuid.v4() },
        data: [],
      };
    }
    row.data.forEach(col => {
      const newCol = {
        attributes: { ...col.attributes, id: uuid.v4() },
        data: cloneDeep(col.data),
      };
      newRow.data.push(newCol);
    });

    rows.push(newRow);
  });

  const tableData = {
    attributes: cloneDeep(oldSection.data.attributes),
    data: rows,
  };

  if (
    oldSection.type !== "GoogleSheet" &&
    !oldSection.data.attributes.isLinkedToDataSource
  ) {
    return {
      ...oldSection,
      id: uuid.v4(),
      label: `${oldSection.label}`,
      header: `${oldSection.header}`,
      data: tableData,
    };
  }
  if (oldSection.data.attributes.isLinkedToDataSource) {
    tableData.attributes.label = `${oldSection.data.attributes.label} Copy`;
    return {
      ...oldSection,
      id: uuid.v4(),
      label: `${oldSection.label} copy`,
      header: `${oldSection.header}`,
      data: tableData,
    };
  }
  return {
    ...oldSection,
    id: uuid.v4(),
    type: DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET,
    lock: false,
    label: `${oldSection.label} copy`,
    header: `${oldSection.header}`,
    data: tableData,
  };
};

export const cloneComponent = (oldSection, mode) => {
  if (
    oldSection.type === DOCUMENT_COMPONENT_TYPES.HTML ||
    oldSection.type === DOCUMENT_COMPONENT_TYPES.IMAGE
  ) {
    const newSection = cloneDeep(oldSection);
    newSection.id = uuid.v4();
    if (mode === "DOCUMENT" && newSection.clauseOptions) {
      newSection.clauseOptions = null;
    }
    return newSection;
  }

  // Table
  if (oldSection.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    const clonedSection = cloneTable(oldSection);
    return updateFormulaWhileCloning(clonedSection);
  }

  // Page Break
  if (oldSection.type === DOCUMENT_COMPONENT_TYPES.PAGE_BREAK) {
    const newSection = cloneDeep(oldSection);
    newSection.id = uuid.v4();
    return newSection;
  }

  // Empty Space
  if (oldSection.type === DOCUMENT_COMPONENT_TYPES.LINE_SPACE) {
    const newSection = cloneDeep(oldSection);
    newSection.id = uuid.v4();
    return newSection;
  }

  // Empty Signature
  if (oldSection.type === DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER) {
    const newSection = cloneDeep(oldSection);
    newSection.data = convertSignersData(newSection.data);
    newSection.id = uuid.v4();
    return newSection;
  }

  // Empty Stripe button
  if (oldSection.type === DOCUMENT_COMPONENT_TYPES.STRIPE_PAYMENT_BUTTON) {
    const newSection = cloneDeep(oldSection);
    newSection.id = uuid.v4();
    return newSection;
  }

  // Empty google sheet
  if (oldSection.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET) {
    const newSection = cloneTable(oldSection);
    return newSection;
  }

  console.error(`Can't clone section of type ${oldSection.type}`);
  return null;
};

export const snaplinkGeneratedInfo = createdTime => {
  if (createdTime) {
    const timeStamp = new Date(createdTime);
    const dateInfo = timeStamp.toString().split(" ");
    const timeZone = timeStamp.toString().split("(")[1].split(")")[0];
    const time = timeStamp.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return `<div>
    This snap link was generated on <b>${dateInfo[1]} ${dateInfo[2]}, ${dateInfo[3]}
    </b> at <b>${time} (${timeZone})</b>. Switch it off and on again to get new snap link with updated content.</div>`;
  }
  return null;
};

export const plublishLinkGeneratedInfo = (createdTime, count) => {
  if (createdTime) {
    const timeStamp = new Date(createdTime);
    const dateInfo = timeStamp.toString().split(" ");
    const timeZone = timeStamp.toString().split("(")[1].split(")")[0];
    const time = timeStamp.toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return `<div>
    This link was generated on <b>${dateInfo[1]} ${dateInfo[2]}, ${dateInfo[3]}
    </b> at <b>${time} (${timeZone})</b>. <b>${count} documents</b> have been created from it. </div>`;
  }
  return null;
};

export const isDocBandAvailabel = (
  stage,
  docRecipientList,
  acceptorsList,
  mode,
  isSharedDocument,
  pendingApprovalRequired,
  docApprovalStatus,
  paymentStatus,
) => {
  const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
  const signerList = [];
  docRecipientList.map(item =>
    item.recipient_type === "SIGNER" ? signerList.push(item) : null,
  );
  let bandStatus = false;
  if (CURRENT_USER && mode === "DOCUMENT" && !isSharedDocument) {
    if (paymentStatus === "Paid") {
      bandStatus = true;
    } else if (stage === "INTERNAL_REVIEW") {
      if (pendingApprovalRequired) {
        bandStatus = true;
      } else if (
        docApprovalStatus[0] &&
        docApprovalStatus[0].workflowStatus === "REJECTED"
      ) {
        bandStatus = true;
      } else if (
        docApprovalStatus[0] &&
        docApprovalStatus[0].workflowStatus === "PENDING"
      ) {
        bandStatus = true;
      }
    } else if (stage === "APPROVED") {
      if (docApprovalStatus && docApprovalStatus.length > 0) {
        bandStatus = true;
      } else {
        bandStatus = true;
      }
    } else if (stage === "SENT") {
      if (acceptorsList && acceptorsList.length > 0) {
        if (
          acceptorsList &&
          acceptorsList.find(
            item =>
              item.user &&
              item.user.id === CURRENT_USER.id &&
              item.recipient_type === "ACCEPTOR" &&
              item.accepting_states === "",
          )
        ) {
          bandStatus = true;
        } else {
          bandStatus = true;
        }
      } else if (signerList && signerList.length > 0) {
        bandStatus = true;
      }
    } else if (stage === "COMPLETED") {
      if (acceptorsList && acceptorsList.length > 0) {
        bandStatus = true;
      } else if (signerList && signerList.length > 0) {
        bandStatus = true;
      }
    } else if (stage === "DECLINED") {
      if (acceptorsList && acceptorsList.length > 0) {
        bandStatus = true;
      } else if (signerList && signerList.length > 0) {
        bandStatus = true;
      }
    } else if (stage === "SENT_FOR_FILLING") {
      bandStatus = true;
    }
  }
  return bandStatus;
};

export const checkIfAnyTableIsLinkedToDataSource = components => {
  let hasLinkedTable = false;
  components.forEach(component => {
    if (Array.isArray(component) && component.length > 0) {
      component.forEach(comp => {
        const isLinked = checkSectionIsTableV2GSheet(comp);
        hasLinkedTable = hasLinkedTable || isLinked;
      });
    } else if (component.content) {
      const isLinked = checkSectionIsTableV2GSheet(component);
      hasLinkedTable = hasLinkedTable || isLinked;
    }
  });
  return hasLinkedTable;
};

export const checkTableDataSource = tableSection => {
  let dataSource;
  if (checkSectionIsLinkedQuoteTable(tableSection)) {
    dataSource = DOCUMENT_COMPONENT_TYPES.DEAL_TABLE;
  } else if (checkSectionIsTableV2GSheet(tableSection)) {
    dataSource = DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET;
  }
  return dataSource;
};

export const addLinkQuoteTableData = component => {
  if (component.type === "Table" && checkSectionIsLinkedQuoteTable(component)) {
    console.debug(component);
    const reduxState = store.getState();
    const { DocumentStore, QuotationStore, DealTableStore } = reduxState;
    const {
      document: { status, isLocked },
      clickedActiveComponentId,
    } = DocumentStore;
    const { dealTableId } = QuotationStore;
    const {
      discount: { [dealTableId]: discount } = {},
      rowData: { [dealTableId]: rowData } = [],
      priceBook: { [dealTableId]: priceBook = {} } = {},
      columnDefs: { [dealTableId]: columnDefs } = [],
      metaData,
    } = DealTableStore;

    // const { dealtableView, showGroupedData, data } = props.section;

    // DECLARATIONS
    const currency = (priceBook && priceBook.currency) || "USD";
    const view = "NORMAL_VIEW";
    // const view =
    //   dealtableView || (showGroupedData ? "GROUPED_VIEW" : "NORMAL_VIEW");
    // calculate group Summary
    // const groupSummary =
    //   data && data instanceof Object && data.groupSummary
    //     ? data.groupSummary
    //     : {};
    // groupSummary = getMergeGroups(discount, groupSummary);
    // const isComponentActive = clickedActiveComponentId === props.section.id;
    const dealTableColumns = getDealTableColumns(columnDefs);
    const ungroupedData = getUngroupedData(
      metaData,
      rowData,
      dealTableColumns,
      null,
      null,
      true,
    );
    const viewData = ungroupedData.map(row => ({
      data: row.map((cell, index) => {
        if (dealTableColumns[index].currency) {
          return numberForamting(cell, CURRENCY_SYMBOL[currency]);
        }
        return cell;
      }),
      attributes: { id: uuid.v4() },
    }));
    component.data.data = viewData;
    return component;
  }
  return component;
};

const getTableText = tableSection => {
  if (
    !tableSection ||
    !tableSection.content ||
    !tableSection.content.data ||
    !tableSection.content.data.data
  ) {
    return null;
  }
  const {
    content: {
      data: { data: tableData },
    },
  } = tableSection;
  let retrivedText = null;
  tableData.some(row => {
    if (row.data) {
      const { data: rowData } = row;
      rowData.some(col => {
        if (col.data) {
          const {
            data: { data: colData },
          } = col;
          if (colData instanceof ContentState) {
            const firstBlock = colData.getFirstBlock();
            if (firstBlock && firstBlock.getText()) {
              retrivedText = firstBlock.getText();
              return true;
            }
          } else if (
            colData &&
            colData.blocks &&
            colData.blocks[0] &&
            colData.blocks[0].text
          ) {
            retrivedText = colData.blocks[0].text;
            return true;
          }
        }
        return false;
      });
    }
    return !!retrivedText;
  });

  return retrivedText;
};

export const getFirstComponentTextHandler = (layout, components) => {
  let text = null;

  layout.some(row => {
    let found = false;
    row.some(compID => {
      const section = components[compID];
      if (!found && section.content) {
        if (section.content.type === DOCUMENT_COMPONENT_TYPES.HTML) {
          text = section.content.header;
          if (text) {
            found = true;
            return true;
          }
        } else if (
          section.content.type === DOCUMENT_COMPONENT_TYPES.TABLE ||
          section.content.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET
        ) {
          text = getTableText(section);
          if (text) {
            found = true;
            return true;
          }
        }
      }
      return false;
    });
    return found;
  });
  return text;
};

export const getFirstComponentText = (layout, components) => {
  let firstComponentText = null;

  // 1. check in coverpage if exists
  const coverPage = Object.keys(components)
    .filter(key => {
      const section = components[key] ? components[key].content : null;
      return section ? section.type === "CoverPage" : false;
    })
    .reduce((obj, key) => {
      obj = components[key];
      return obj;
    }, {});

  if (
    coverPage &&
    coverPage.content &&
    coverPage.content.data &&
    coverPage.content.data.layout &&
    Array.isArray(coverPage.content.data.layout) &&
    coverPage.content.data.components
  ) {
    firstComponentText = getFirstComponentTextHandler(
      coverPage.content.data.layout,
      coverPage.content.data.components,
    );
  }
  // 2. check in body
  if (!firstComponentText) {
    firstComponentText = getFirstComponentTextHandler(layout, components);
  }

  if (!firstComponentText || firstComponentText.trim().length === 0) {
    return null;
  }

  firstComponentText = firstComponentText.split(/\s+/).slice(0, 3).join(" "); // first 3 words
  firstComponentText = firstComponentText.trim().substring(0, 100); // max 100chars
  return firstComponentText;
};
